// Queue pages that filter returns by status for each role
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { StatusBadge } from "../components/StatusBadge";

export default function QueuePage({ title, subtitle, statuses, testId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Stable key so we don't re-fetch unless the *contents* of statuses change.
  const statusKey = useMemo(() => (statuses || []).join(","), [statuses]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = [];
      for (const s of statuses || []) {
        const { data } = await api.get("/returns", { params: { status: s } });
        all.push(...data);
      }
      all.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setRows(all);
    } finally {
      setLoading(false);
    }
    // We deliberately depend on statusKey (a primitive) rather than the array,
    // to avoid re-fetching when a parent re-renders with a new array identity
    // but identical contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusKey]);

  useEffect(() => { load(); }, [load]);

  const renderBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="text-center text-slate-500 py-8">Loading…</TableCell>
        </TableRow>
      );
    }
    if (rows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="text-center text-slate-500 py-8">Nothing pending. 🎉</TableCell>
        </TableRow>
      );
    }
    return rows.map((r) => (
      <TableRow key={r.id} className="hover:bg-slate-50">
        <TableCell className="font-mono font-medium text-orange-700">{r.request_number}</TableCell>
        <TableCell className="text-sm text-slate-600">{(r.created_at || "").slice(0, 10)}</TableCell>
        <TableCell>{r.customer_name}</TableCell>
        <TableCell className="text-sm max-w-[260px] truncate">{(r.items || []).map((i) => i.item_name).join(", ")}</TableCell>
        <TableCell><StatusBadge status={r.status} /></TableCell>
        <TableCell className="text-right">
          <Link to={`/returns/${r.id}`} className="text-orange-700 hover:underline text-sm font-medium" data-testid={`process-${r.request_number}`}>Process →</Link>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <div className="space-y-6" data-testid={testId}>
      <div>
        <h1 className="heading text-3xl font-bold text-slate-900">{title}</h1>
        <p className="text-slate-500 mt-1">{subtitle} · {rows.length} item(s)</p>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Request No</TableHead><TableHead>Date</TableHead><TableHead>Customer</TableHead>
              <TableHead>Items</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {renderBody()}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
